import z from 'zod';
import { statusPackWarningsSchema } from './status-pack-warning.schema.js';
export const adStatusEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('ad-status-events-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents. Follows `pma-der-events-ready` /
    // `document-header-ready` rather than the looser `project-progress` / `project-data-ready`
    // spelling.
    //
    // Required. Brain always emits this stamp. An envelope without it does not parse (DR-3341).
    generatedAt: z.iso.datetime(),
    // DR-3342: persisted per-project counter from the finalization write. Optional so a
    // consumer upgrading ahead of Brain still accepts older envelopes. Brain always emits
    // it. Prefer `generation` over `generatedAt` when present; after a generation-bearing
    // snapshot is applied, ignore generationless envelopes.
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    documentId: z.string(),
    pageIds: z.array(z.string()),
    ads: z.array(z.looseObject({
        adNumber: z.string(),
        euad: z.string().optional(),
        // DR-2243: every authority's AD reference for this row (parallel per-authority
        // columns, e.g. "CAA AD No." beside "FAA AD No."). Ordered PRIMARY FIRST, then the
        // remaining references in printed order; adReferences[0] is the primary and mirrors
        // adNumber/issuingAuthority, the EASA entry mirrors euad. Omitted on older persisted
        // rows / the retired v4 path (trio-only).
        adReferences: z
            .array(z.looseObject({
            authority: z.string(),
            number: z.string(),
            revision: z.string().optional(),
        }))
            .optional(),
        // DR-1747: issuing regulator / source database, derived from the row's regulator
        // column/section, so oil can scope the AD lookup by authority.
        issuingAuthority: z.string().optional(),
        revision: z.string().optional(),
        // DR-3702: printed requirement-line label ("REQ 1" / "REQ 2") peeled off adNumber by the
        // sanitiser when a sheet lists ONE AD as several requirement rows. Declared explicitly
        // rather than left to the loose object, so it is part of the typed wire contract: oil
        // groups the rows under one AD instead of collapsing two rows sharing an adNumber.
        // DR-4145: also carries the TRAX per-control-item task index as "TASK <nn>", peeled off a
        // "YYYY-MM-DD-FAA-NN" identifier. DR-4166 carries the operator's paragraph tag as
        // "PAR <x>" ("PAR.(2)" -> "PAR 2"). DR-5719 carries the shop / operator part marker as
        // "PART <x>" (word form "2015-0133-PART A", glued "P3" on an FAA number) and the glued
        // paragraph "J3" on an FAA number as "PAR J3". The value set is "REQ <label>" | "INSP" |
        // "TASK <nn>" | "PAR <x>" | "PART <x>". DR-5777: an FAA shop part letter ("AD2002-13-03K",
        // "AD 2004-12-08 B") is sent as "PART <letter>", no longer in `revision`; `revision` is
        // omitted when the letter was its only content. DR-5923: `PAR <x>` and `REQ <x>` rows now fold
        // into the AD's one row as `paragraphNotes` (as `PART <x>` rows fold into `partNotes` since
        // DR-5778), so the published row carries none of those three; `TASK <nn>` and `INSP` still ship.
        // DR-6221: an AMOS engineering-control item of the AD, printed as a number after it on a WestJet
        // P/N Last Compliance Print ("AD 2011-18-10 8000"), is sent as "EC <item>" ("EC 8000") and
        // folds into `paragraphNotes` the same way.
        requirement: z.string().optional(),
        status: z.string().optional(),
        // DR-3306: document-stated one-time vs repetitive nature. Oil persists
        // `ad.isRepeated ?? null` onto pipeline_ad_compliance_items.is_repeated.
        // DR-5452: also true from a printed repeat interval; a printed one-time mark wins.
        isRepeated: z.boolean().optional(),
        // DR-2614/DR-2597: contradiction flag between the row's status and its Remarks text.
        remarksStatus: z.string().optional(),
        subject: z.string().optional(),
        sbReference: z.string().optional(),
        // DR-2238: catch-all superset of ALL document references on the row; sbReference holds
        // only validated SB-shaped identifiers, rejected tokens migrate here.
        reference: z.array(z.string()).optional(),
        effectiveDate: z.string().optional(),
        // DR-2050: ATA chapter + per-part PN/SN for per-part AD status rows (AMOS
        // Modification Control Reports, GE/CFM Remarks-embedded PN:/SN: tokens).
        ataReference: z.string().optional(),
        applicableEngine: z.string().optional(),
        partNumber: z.string().optional(),
        serialNumber: z.string().optional(),
        // DR-3995: per-part sub-rows of ONE AD record (Rinoj 2026-09-10). Present only when the
        // project rollup folded two or more per-part sibling rows of the same AD into this row;
        // then `partNumber`/`serialNumber` on the row itself are absent and every part lives here.
        // Key = (partNumber, serialNumber). A missing `status` means the sheet printed none — map
        // it to blank, NOT to "unknown". No TSN/CSN on a sub-row.
        parts: z
            .array(z.looseObject({
            partNumber: z.string(),
            serialNumber: z.string().optional(),
            status: z.string().optional(),
            complianceDate: z.string().optional(),
        }))
            .optional(),
        // DR-5778 (Rinoj 2026-10-26: one row per AD, parts as a note): the `PART <x>` rows of this AD
        // that folded into this one row. A row Brain used to publish with `requirement: PART <x>` is now
        // this bare AD row, and each part lives here: its printed token, its own status, compliance date,
        // remarks, P/N and S/N (each omitted when the part row states none) and its pages. One entry
        // per distinct part statement (part, status, date, remarks, part number, serial); `pageIds`
        // lists every page that printed it. Single letters first, then numbers, then the rest. Absent when the AD has none; never null or [].
        // A note taken from an older sheet that became history can cite a page outside the payload
        // `pageIds`. `occurrences[]` still lists each `PART <x>` row with its requirement; the
        // published row has none.
        partNotes: z
            .array(z.looseObject({
            part: z.string(),
            status: z.string().optional(),
            complianceDate: z.string().optional(),
            remarks: z.string().optional(),
            partNumber: z.string().optional(),
            serialNumber: z.string().optional(),
            pageIds: z.array(z.string()),
        }))
            .optional(),
        // DR-5923 (Rinoj: one row per AD, its paragraphs or requirements as sub-rows, each with its own
        // status and source): the `PAR <x>` / `REQ <x>` paragraphs of this AD, one entry per label, each
        // as its CURRENT document states it — the latest dated document that prints it; a paragraph a
        // newer document omits keeps its older statement. The row's status is the worst current
        // paragraph: any open -> open; all closed or N/A -> closed; an unknown or blank paragraph beside
        // a settled one -> open. `reconstructedDocumentId` / `documentDate` name the statement's document
        // and `pageIds` its pages. Each optional key is omitted when the statement states none. Sorted
        // PAR before REQ, then naturally by label (PAR A < PAR B; REQ 2 < REQ 10). DR-6221: an AMOS
        // engineering-control item of the AD is an `EC <item>` entry ("EC 8000"), sorted after REQ
        // (EC 2000 < EC 8000). Absent when the AD has none; never null or []. `occurrences[]` still
        // lists each paragraph row with its requirement.
        paragraphNotes: z
            .array(z.looseObject({
            paragraph: z.string(),
            status: z.string().optional(),
            complianceDate: z.string().optional(),
            complianceTsn: z.number().optional(),
            complianceCsn: z.number().optional(),
            complianceShopVisit: z.string().optional(),
            nextDueDate: z.string().optional(),
            nextDueCycles: z.number().optional(),
            nextDueHours: z.number().optional(),
            nextDueShopVisit: z.string().optional(),
            nextDueAtShopVisit: z.boolean().optional(),
            // DR-6111: the paragraph's printed remaining count (see the row field).
            remainingCycles: z.number().optional(),
            remainingHours: z.number().optional(),
            // DR-6112: the paragraph's own statement says not performed (see the row field).
            notPerformed: z.literal(true).optional(),
            remarks: z.string().optional(),
            pageIds: z.array(z.string()),
            reconstructedDocumentId: z.string(),
            documentDate: z.string().optional(),
        }))
            .optional(),
        complianceDate: z.string().optional(),
        complianceTsn: z.number().optional(),
        complianceCsn: z.number().optional(),
        complianceShopVisit: z.string().optional(),
        nextDueCycles: z.number().optional(),
        nextDueHours: z.number().optional(),
        nextDueDate: z.string().optional(),
        nextDueShopVisit: z.string().optional(),
        nextDueAtShopVisit: z.boolean().optional(),
        // DR-6111: the count of cycles / flight hours still to run until the next compliance, AS THE
        // SHEET PRINTS IT ("required within 1598 Cycles" -> `remainingCycles: 1598`), measured from the
        // sheet's own counters. The count is the row's own, from the winning document (never filled
        // from another document); every document's count also rides its own `occurrences[]` entry.
        // Resolve the basis through `remainingReconstructedDocumentId`: the document (and its
        // `occurrences[]` entry) with that id, whose header TSN / CSN / as-of date
        // `document-header-ready` carries, not `adPageIds`. Brain does no arithmetic. Absent when the sheet prints an absolute due or no due;
        // never null. Open rows only. A printed count is never in both a remaining lane and the matching
        // nextDue lane, except on a zero-time engine: a page header counter of exactly 0 may keep an
        // equal absolute beside it. A remaining count of 0 is not representable (absent).
        remainingCycles: z.number().optional(),
        remainingHours: z.number().optional(),
        // DR-6111: the reconstructed document the remaining count was read from (the same id its
        // `occurrences[]` entry carries). Present exactly when `remainingCycles` or `remainingHours` is.
        remainingReconstructedDocumentId: z.string().optional(),
        repeatIntervalCycles: z.number().optional(),
        repeatIntervalHours: z.number().optional(),
        repeatIntervalDays: z.number().optional(),
        repeatIntervalShopVisits: z.number().optional(),
        // DR-2063: document-sourced supersession/cancellation references (AMOS "Replacement
        // Information" sub-block, free-text Remarks/Method-of-Compliance) so oil can cross-check
        // against the AD-reference catalog's supersedes/supersededBy chain.
        supersededByRef: z.string().optional(),
        cancelledByRef: z.string().optional(),
        supersedesRef: z.string().optional(),
        // DR-5187: bare AD numbers of the older ADs this row's AD terminates (AMOS "This Document
        // terminates <ref>/AD Rev.NN"), printed order, deduped, absent when none. A termination is
        // not a supersession: the terminated AD stays in force, satisfied by this AD's work. For Oil:
        // when a terminated AD has no row of its own and a closed AD lists it here, show
        // "Terminated by <that AD>" (never "Superseded by"); the AD's own status wins when any
        // sheet prints one.
        terminatesRefs: z.array(z.string()).optional(),
        remarks: z.string().optional(),
        adPageIds: z.array(z.string()),
        // DR-4271: two or more distinct FAA biweekly readings were seen for one published row.
        // Advisory. DR-4348: `adReferences` keeps the surviving row's reading; a conflicting
        // reading from an absorbed row is listed here only. Omitted when at most one was seen.
        faaNumberConflict: z
            .looseObject({
            numbers: z.array(z.string()).min(2),
        })
            .optional(),
        // DR-4984: who issued this row's SOURCE document, read from its header. Tri-state
        // `issuerRole`: absent means the document states no signal, or (DR-5044) the issuer is an
        // independent third party that is neither the operator nor a shop; Brain never defaults it,
        // the consumer applies its own default. `issuerName` / `workOrder` are verbatim header values.
        issuerRole: z.enum(['operator', 'shop']).optional(),
        issuerName: z.string().optional(),
        workOrder: z.string().optional(),
        // DR-5078: `'failed'` when the source document's header extraction failed and its pages agree
        // on no `issuerRole`: an absent role then means "not read", not "not stated".
        provenanceStatus: z.enum(['failed']).optional(),
        // DR-5550: is this row's SOURCE document signed or stamped? Document-scoped: `true` when at
        // least one page of the document shows a completed signature or stamp, `false` when every
        // page was read and none does. Not line certification (the job-card `signedOff` is).
        // Tri-state: `true` signed, `false` read and not signed, absent = unknown, never `null`.
        // Unlike `compliance-job-card-facts-ready`, where `false` and `null` both mean 'not
        // certified', absent here is NOT 'not signed'. A non-boolean value parses as absent.
        signedOff: z.boolean().optional().catch(undefined),
        // DR-5551 (Rinoj 2026-10-21): this closed line itself states the work was complied with BEFORE
        // this shop visit ("Previously Complied With", PW, PE, PC, PCW, ASY, PREV, or a remarks phrase
        // such as "C/W prior to induction" / "done at previous shop visit"). Additive, not a status
        // change: `status` stays as printed (closed). `quote` is the status cell as printed, or the
        // remarks sentence that carried the wording. Row evidence: it travels only with its own line.
        // Absent = no such wording, or unknown. A malformed value parses as absent.
        compliedBeforeVisit: z.looseObject({ quote: z.string() }).optional().catch(undefined),
        // DR-6112: the sheet's own status cell ("NP", "Not performed") or this line's own remark ("refer
        // to Open Item List for further action") states the work was NOT done at this visit. Additive,
        // not a status change: `status` stays open. True-only: `true` or absent, never `false`. Row
        // evidence, never filled from another row or document; a missing compliance date never sets it,
        // and a remark stating the work was done (`remarksStatus: 'closed'`) clears it. Oil writes
        // `notPerformed ?? null`.
        notPerformed: z.literal(true).optional(),
        // DR-6112: the sheet's status cell VERBATIM whenever the line prints one ("N.P.", "Complied w/",
        // "NA"): whitespace collapsed, casing and punctuation kept. A placeholder cell such as `N/A` or
        // `-` is kept as printed; absent (never null or '') only when the line prints no status cell.
        // Never changes `status`; it travels only with the status it was printed beside. Not on
        // `paragraphNotes[]`.
        printedStatus: z.string().optional(),
        // DR-5051 (Rinoj 2026-10-13): an older source document closed this AD and the newest shows it
        // open. The row keeps `status` open; this names the newest earlier closure (by
        // `complianceDate`) and the reconstructed document that stated it. Sent only on an open row.
        priorCompliance: z
            .looseObject({
            complianceDate: z.string().optional(),
            complianceTsn: z.number().optional(),
            complianceCsn: z.number().optional(),
            complianceShopVisit: z.string().optional(),
            reconstructedDocumentId: z.string(),
            documentId: z.string().optional(),
            documentDate: z.string().optional(),
        })
            .optional(),
        // DR-5873 (Rinoj 2026-10-27): the leading AD sheet does not list this row's AD (its own AD
        // number, at any revision); the row comes from an older document at that document's own
        // status. Show "Not on latest AD sheet". Sent only as `true`; absent means the leading sheet
        // lists the AD.
        notOnLatestSheet: z.boolean().optional(),
    })),
    // DR-4456: review flags on the published pack (see `statusPackWarningsSchema`).
    warnings: statusPackWarningsSchema,
    // DR-4984: every usable AD row of every source document, taken BEFORE the cross-document
    // consolidation and the per-part fold that shape `ads`. One entry per row per document, with that
    // document's own issuer provenance, for proof-found / proof-missing and the `workOrder` join to the
    // release certificate. Brain always sends it — `[]` when no row survives, never absent — so an
    // absent array means an older Brain. Optional only so this schema still parses those envelopes.
    occurrences: z
        .array(z.looseObject({
        reconstructedDocumentId: z.string(),
        documentId: z.string().optional(),
        pageIds: z.array(z.string()),
        adNumber: z.string(),
        // `adNumber` with the revision stripped; absent when no base resolves.
        adBaseNumber: z.string().optional(),
        issuingAuthority: z.string().optional(),
        euad: z.string().optional(),
        revision: z.string().optional(),
        // DR-5031: the same identity strings `ads` already carries. Optional, so an older
        // occurrence still parses. Oil uses them to join a proof row to its AD.
        requirement: z.string().optional(),
        adReferences: z
            .array(z.looseObject({
            authority: z.string(),
            number: z.string(),
            revision: z.string().optional(),
        }))
            .optional(),
        status: z.string(),
        remarksStatus: z.string().optional(),
        subject: z.string().optional(),
        sbReference: z.string().optional(),
        complianceDate: z.string().optional(),
        complianceTsn: z.number().optional(),
        complianceCsn: z.number().optional(),
        complianceShopVisit: z.string().optional(),
        // DR-6111: this document's printed remaining count (see the row field). Its basis is this
        // occurrence's document header (`document-header-ready`). Absent when none; never null.
        remainingCycles: z.number().optional(),
        remainingHours: z.number().optional(),
        partNumber: z.string().optional(),
        serialNumber: z.string().optional(),
        issuerRole: z.enum(['operator', 'shop']).optional(),
        issuerName: z.string().optional(),
        workOrder: z.string().optional(),
        provenanceStatus: z.enum(['failed']).optional(),
        // DR-5550: is this occurrence's document signed or stamped? Document-scoped: `true` when at
        // least one page of the document shows a completed signature or stamp, `false` when every
        // page was read and none does. Not line certification (the job-card `signedOff` is).
        // Tri-state: `true` signed, `false` read and not signed, absent = unknown, never `null`.
        // Unlike `compliance-job-card-facts-ready`, where `false` and `null` both mean 'not
        // certified', absent here is NOT 'not signed'. A non-boolean value parses as absent.
        signedOff: z.boolean().optional().catch(undefined),
        // DR-5551: this document's line states compliance before this shop visit (see the row field).
        // Additive, not a status change. Absent = no such wording, or unknown. A malformed value parses
        // as absent.
        compliedBeforeVisit: z.looseObject({ quote: z.string() }).optional().catch(undefined),
        // DR-6112: this document's line states the work was not done at this visit (see the row
        // field). True-only. Oil writes `notPerformed ?? null`.
        notPerformed: z.literal(true).optional(),
        // DR-6112: this document's status cell verbatim (see the row field).
        printedStatus: z.string().optional(),
    }))
        .optional(),
});
//# sourceMappingURL=ad-status-events-ready.webhook.js.map